import { useRef, useState } from 'react';
import { Editor } from '@hugerte/hugerte-react';

// Utils
import cn from '../../../utils/cn';

// Components
import { Skeleton } from '../Skeleton/Skeleton';

// Styles
import styles from './TinyMceEditor.module.css';

/**
 * TinyMceEditor - Pure TinyMCE editor component
 * @param {object} props
 * @param {string} [props.value] - Current editor value (HTML)
 * @param {(value: string) => void} props.onChange - Change handler
 * @param {string} [props.name] - Input name attribute
 * @param {boolean} [props.disabled] - Whether editor is disabled
 * @param {string} [props.error] - Validation error
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder] - Placeholder text
 * @param {number} [props.height=300] - Editor height in pixels
 * @param {Function} [props.onBlur] - Blur handler
 * @returns {JSX.Element} TinyMceEditor component
 */
function TinyMceEditor({
  value = '',
  onChange,
  name,
  disabled = false,
  error = '',
  className = '',
  placeholder = '',
  height = 300,
  onBlur,
}) {
  // Store initial value - editor will be uncontrolled after mount
  const initialValueRef = useRef(value);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Handle editor initialization
   */
  const handleInit = () => {
    // Editor is fully initialized, hide loading state
    setIsLoading(false);
  };

  /**
   * Handle editor content change
   * @param {string} content - Editor content (HTML)
   */
  const handleEditorChange = (content) => {
    if (onChange) {
      onChange(content);
    }
  };

  /**
   * Handle editor blur
   */
  const handleEditorBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  /** @type {any} */
  const editorConfig = {
    height,
    force_br_newlines: false,
    force_p_newlines: false,
    forced_root_block: '',
    convert_urls: false,
    entity_encoding: 'raw',
    menubar: 'file edit view insert format tools table',
    plugins: [
      'advlist',
      'autolink',
      'lists',
      'link',
      'image',
      'charmap',
      'preview',
      'anchor',
      'searchreplace',
      'visualblocks',
      'code',
      'fullscreen',
      'insertdatetime',
      'media',
      'table',
      'emoticons',
      'wordcount',
    ],
    toolbar: [
      'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | '
      + 'blocks fontfamily fontsize undo redo | cut copy paste bullist numlist outdent indent '
      + 'blockquote removeformat subscript superscript',
      'link image media | forecolor backcolor emoticons | preview code',
    ],
    content_style:
      'body { font-family:Arial,sans-serif; font-size:9pt }',
    placeholder,
    block_formats: 'Paragraphe=p; Titre 1=h1; Titre 2=h2; Titre 3=h3; Titre 4=h4; Titre 5=h5; Titre 6=h6; Préformaté=pre',
    font_family_formats: 'Andale Mono=andale mono,times;'
  + 'Arial=arial,helvetica,sans-serif;'
  + 'Arial Black=arial black,avant garde;'
  + 'Book Antiqua=book antiqua,palatino;'
  + 'Comic Sans MS=comic sans ms,sans-serif;'
  + 'Courier New=courier new,courier;'
  + 'Georgia=georgia,palatino;'
  + 'Helvetica=helvetica;'
  + 'Impact=impact,chicago;'
  + 'Symbol=symbol;'
  + 'Tahoma=tahoma,arial,helvetica,sans-serif;'
  + 'Terminal=terminal,monaco;'
  + 'Times New Roman=times new roman,times;'
  + 'Trebuchet MS=trebuchet ms,geneva;'
  + 'Verdana=verdana,geneva;'
  + 'Webdings=webdings;'
  + 'Wingdings=wingdings,zapf dingbats',
    font_size_formats: '8pt 9pt 10pt 12pt 14pt 18pt 24pt 36pt',
    language: 'fr_FR',
    language_url: '/tinyMce/langs/fr_FR.js',
    font_family: 'Arial',
    font_size: '9pt',
    setup: (/** @type {any} */ editor) => {
      editor.on('init', () => {
        setIsLoading(false);
        if (!disabled) {
          setTimeout(() => {
            editor.focus();
          }, 0);
        }
      });
      editor.on('blur', handleEditorBlur);
    },
  };

  return (
    <div
      className={cn([
        styles.wrapper,
        error && styles['has-error'],
        disabled && styles['is-disabled'],
        isLoading && styles['is-loading'],
        className,
      ])}
      data-name={name}
    >
      {isLoading && (
        <div className={styles['loading-overlay']}>
          <Skeleton
            shape="rectangle"
            width="100%"
            height="39px"
            theme="default"
            noAnimation={false}
          />
          <Skeleton
            shape="rectangle"
            width="100%"
            height="39px"
            theme="default"
            noAnimation={false}
          />
          <Skeleton
            shape="rectangle"
            width="100%"
            height="240px"
            theme="default"
            noAnimation={false}
          />
        </div>
      )}
      <div className={cn([styles['editor-container'], isLoading && styles['editor-hidden']])}>
        <Editor
          onInit={handleInit}
          onEditorChange={handleEditorChange}
          initialValue={initialValueRef.current || ''}
          disabled={disabled}
          init={editorConfig}
        />
      </div>

      {error && (
        <div className={styles.error} role="alert" aria-live="polite">
          {error}
        </div>
      )}
    </div>
  );
}

export default TinyMceEditor;

// Legacy configuration Heeds
// // Build plugins array matching legacy config
// const plugins = [
//   'advlist',
//   'autolink',
//   'link',
//   'image',
//   'lists',
//   'charmap',
//   'print',
//   'preview',
//   'hr',
//   'anchor',
//   'pagebreak',
//   'spellchecker',
//   'searchreplace',
//   'wordcount',
//   'visualblocks',
//   'visualchars',
//   'code',
//   'fullscreen',
//   'insertdatetime',
//   'media',
//   'nonbreaking',
//   'save',
//   'table',
//   'contextmenu',
//   'directionality',
//   'emoticons',
//   'template',
//   'paste',
//   'textcolor',
// ];

// // Add fullpage plugin if mail editor (legacy behavior)
// if (isMailEditor) {
//   plugins.unshift('fullpage');
// }

// // Legacy toolbar configuration
// const toolbar = 'bold italic underline strikethrough alignleft aligncenter alignright alignjustify '
//   + 'styleselect formatselect fontselect fontsizeselect | '
//   + 'undo redo cut copy paste bullist numlist outdent indent blockquote undo redo '
//   + 'removeformat subscript superscript | insertfile | link image media | '
//   + 'forecolor backcolor emoticons | preview code';

// const editorConfig = {
//   height,
//   // Legacy config: no forced root block, no forced p/br newlines
//   force_br_newlines: false,
//   force_p_newlines: false,
//   forced_root_block: '',
//   // Legacy config: convert URLs disabled, raw entity encoding
//   convert_urls: false,
//   // @ts-ignore - entity_encoding 'raw' is valid in TinyMCE but TypeScript types may not include it
//   entity_encoding: 'raw',
//   plugins,
//   toolbar,
//   font_formats: fontFormats,
//   // Legacy content CSS (external file if provided, otherwise no inline style)
//   content_css: contentCss || undefined,
